
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Spinner } from '@/components/Spinner';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const formSchema = z.object({
  email: z.string().email("Por favor, introduce un email válido")
});

const PasswordReset = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ""
    }
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[PasswordReset] Sending recovery link for:', values.email);
      
      // Clear any existing session before sending link
      await supabase.auth.signOut({ scope: 'local' });
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Use the current domain for redirect URL - ensure it's the update-password page
      const currentDomain = window.location.origin;
      const redirectUrl = `${currentDomain}/update-password`;
      
      console.log('[PasswordReset] Redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error("[PasswordReset] Error sending recovery link:", error);
        
        if (error.message.includes('rate limit')) {
          setError("Has solicitado muchos enlaces de recuperación. Por favor, espera unos minutos antes de intentar de nuevo.");
        } else {
          // For security, always show success even if email doesn't exist
          console.log("[PasswordReset] Showing success for security");
          setIsSubmitted(true);
        }
        
        if (!error.message.includes('rate limit')) {
          return;
        }
      } else {
        console.log('[PasswordReset] Recovery link sent successfully');
        setIsSubmitted(true);
        
        toast({
          title: "Enlace de recuperación enviado",
          description: "Por favor, revisa tu correo electrónico.",
          variant: "default"
        });
      }
      
    } catch (error: any) {
      console.error("[PasswordReset] Unexpected error:", error);
      setError("Error inesperado al enviar el enlace. Por favor, inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Recuperar Contraseña</CardTitle>
          <CardDescription className="text-center">
            Introduce tu email para recibir un enlace de recuperación
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        
          {isSubmitted ? (
            <div className="text-center space-y-4">
              <p className="text-green-600">Enlace de recuperación enviado.</p>
              <p>Por favor, revisa tu correo electrónico y sigue las instrucciones para restablecer tu contraseña.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="text-blue-800 font-medium mb-2">Importante:</p>
                <ul className="text-blue-700 space-y-1">
                  <li>• El enlace expira en 1 hora</li>
                  <li>• Revisa tu carpeta de spam si no ves el correo</li>
                  <li>• El enlace solo puede usarse una vez</li>
                  <li>• Haz clic directamente en el enlace del email</li>
                  <li>• No copies y pegues el enlace, úsalo directamente</li>
                </ul>
              </div>
              <Button 
                onClick={() => navigate('/login')} 
                className="mt-4 bg-vegana-primary hover:bg-vegana-accent"
              >
                Volver al inicio de sesión
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="tu@email.com" {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-vegana-primary hover:bg-vegana-accent" 
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" /> : "Enviar enlace de recuperación"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <div className="text-sm text-center">
            <Link to="/login" className="text-vegana-primary hover:text-vegana-accent">
              Volver al inicio de sesión
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PasswordReset;
